import { X, Plus, Save } from "lucide-react";
import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { DeclarationDocumentsTableContent } from './DeclarationDocumentsTableContent';
import { useSelector, useDispatch } from 'react-redux';
import { setExtractedData, setLastEditedDocumentField, clearLastEditedDocumentField } from '../../../store/reducers/index';
import { getDeclarationSummaryTranslation, getMainTranslation } from '../../../hooks/useLanguage';
import { deepClone } from '../../../utils/dataHelpers';

export const DeclarationDocumentsTableModal = ({
  isOpen,
  onClose,
  declarationIndex,
  isSpanish,
  declaration,
  editableData,
  setEditableData,
  setIsDirty,
}) => {
  const [openDocModalIndex, setOpenDocModalIndex] = useState(null);
  const [openTipoModal, setOpenTipoModal] = useState({});
  const [originalDeclarationData, setOriginalDeclarationData] = useState(null);
  const [isModalDirty, setIsModalDirty] = useState(false);
  const prevIsOpenRef = useRef(false);
  const prevEditableDataRef = useRef(null);
  const isInternalChangeRef = useRef(false);
  const dispatch = useDispatch();
  const company = useSelector((state) => state.company);
  const userData = useSelector((state) => state.user.userData);
  const companyName = company?.nombre?.toLowerCase() || userData?.first_name?.toLowerCase();
  
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      const currentDeclaration = editableData[declarationIndex];
      if (currentDeclaration) {
        const documentos = deepClone(currentDeclaration.documentos || []);
        setOriginalDeclarationData(documentos);
        prevEditableDataRef.current = JSON.stringify(documentos);
        setIsModalDirty(false);
      }
    } else if (!isOpen && prevIsOpenRef.current) {
      setOriginalDeclarationData(null);
      prevEditableDataRef.current = null;
      setIsModalDirty(false);
      isInternalChangeRef.current = false;
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, declarationIndex, editableData]);
  
  useEffect(() => {
    if (isOpen && originalDeclarationData !== null && !isInternalChangeRef.current) {
      const currentDeclaration = editableData[declarationIndex];
      if (currentDeclaration) {
        const currentDocumentos = currentDeclaration.documentos || [];
        const currentDataString = JSON.stringify(currentDocumentos);
        const prevDataString = prevEditableDataRef.current;
        
        if (currentDataString !== prevDataString) {
          setOriginalDeclarationData(deepClone(currentDocumentos));
          prevEditableDataRef.current = currentDataString;
          setIsModalDirty(false);
        }
      }
    }
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
    }
  }, [editableData, isOpen, declarationIndex, originalDeclarationData]);

  useEffect(() => {
    if (isOpen && originalDeclarationData !== null) {
      const currentDeclaration = editableData[declarationIndex];
      if (currentDeclaration) {
        const currentDocumentos = currentDeclaration.documentos || [];
        const hasChanges = JSON.stringify(currentDocumentos) !== JSON.stringify(originalDeclarationData);
        setIsModalDirty(hasChanges);
      }
    }
  }, [isOpen, editableData, declarationIndex, originalDeclarationData]);

  const handleSave = () => {
    isInternalChangeRef.current = true;
    dispatch(setExtractedData(editableData));
    setEditableData(editableData);
    setIsDirty(false);
    
    const currentDeclaration = editableData[declarationIndex];
    if (currentDeclaration) {
      const documentos = deepClone(currentDeclaration.documentos || []);
      setOriginalDeclarationData(documentos);
      prevEditableDataRef.current = JSON.stringify(documentos);
      setIsModalDirty(false);
    }
    toast.success(getMainTranslation('changesSaved', isSpanish));
  };

  const handleCancel = () => {
    if (originalDeclarationData === null) {
      setIsModalDirty(false);
      dispatch(clearLastEditedDocumentField());
      return;
    }
    
    isInternalChangeRef.current = true;
    const updatedData = deepClone(editableData);
    const declaration = updatedData[declarationIndex];
    
    if (declaration) {
      declaration.documentos = deepClone(originalDeclarationData);
      prevEditableDataRef.current = JSON.stringify(declaration.documentos || []);
      setEditableData(updatedData);
    }
    
    setIsModalDirty(false);
    dispatch(clearLastEditedDocumentField());
    toast.success(getMainTranslation('changesCancelled', isSpanish));
  };
  
  const handleSetEditableData = (newData) => {
    isInternalChangeRef.current = true;
    setEditableData(newData);
  };

  const handleAddY025TuDeFrigo = () => {
    isInternalChangeRef.current = true;
    const updatedData = deepClone(editableData);
    if (!updatedData[declarationIndex].documentos)
      updatedData[declarationIndex].documentos = [];
    updatedData[declarationIndex].documentos.push({
      codigo_documento: 'Y025',
      tipo: 1,
      numero: 'ESAEOF240000022A',
      fecha: '2025-10-30',
    });
    setEditableData(updatedData);
    setIsDirty(true);
    dispatch(setLastEditedDocumentField({
      field: '__declaration_documents__',
      value: null,
      declarationIndex,
      partidaIndex: null,
      documentIndex: updatedData[declarationIndex].documentos.length - 1,
    }));
  };

  const handleAddY025ElMosca = () => {
    isInternalChangeRef.current = true;
    const updatedData = deepClone(editableData);
    if (!updatedData[declarationIndex].documentos)
      updatedData[declarationIndex].documentos = [];
    updatedData[declarationIndex].documentos.push({
      codigo_documento: 'Y025',
      tipo: 1,
      numero: 'ESAEOF240000022A',
      fecha: '2025-10-30',
    });
    setEditableData(updatedData);
    setIsDirty(true);
    dispatch(setLastEditedDocumentField({
      field: '__declaration_documents__',
      value: null,
      declarationIndex,
      partidaIndex: null,
      documentIndex: updatedData[declarationIndex].documentos.length - 1,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
      <div className="bg-white rounded-lg p-6 w-full max-w-[95vw] h-[90vh] flex flex-col animate-modalEnter">
        <div className="flex justify-between items-center mb-4 pb-4 border-b">
          <h2 className="text-2xl font-bold">
            {!isSpanish ? "Export Level Documents" : "Documentos Nivel Exportación"} ({declaration.documentos?.length || 0})
          </h2>
          <div className="flex items-center gap-2">
            {isModalDirty && (
              <>
                <button
                  className="bg-red-50 rounded-full flex items-center py-2 px-3 gap-2"
                  onClick={handleCancel}
                  title={!isSpanish ? "Cancel Changes" : "Cancelar Cambios"}
                >
                  <X className="size-4 stroke-red-500" />
                  <span className="text-xs text-red-500">{getDeclarationSummaryTranslation('cancel', isSpanish)}</span>
                </button>
                <button
                  className="bg-primary rounded-full flex items-center py-2 px-3 gap-2"
                  onClick={handleSave}
                  title={!isSpanish ? "Save Changes" : "Guardar Cambios"}
                >
                  <Save className="size-4" />
                  <span className="text-xs">{getDeclarationSummaryTranslation('save', isSpanish)}</span>
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title={!isSpanish ? "Close" : "Cerrar"}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex gap-2 items-center mb-4 justify-end">
          <button
            className="py-1 text-xs flex items-center gap-1"
            onClick={() => {
              isInternalChangeRef.current = true;
              const updatedData = deepClone(editableData);
              if (!updatedData[declarationIndex].documentos)
                updatedData[declarationIndex].documentos = [];
              updatedData[declarationIndex].documentos.push({
                codigo_documento: "",
                tipo: "",
                numero: "",
                fecha: "",
              });
              setEditableData(updatedData);
              setIsDirty(true);
              dispatch(setLastEditedDocumentField({
                field: '__declaration_documents__',
                value: null,
                declarationIndex,
                partidaIndex: null,
                documentIndex: updatedData[declarationIndex].documentos.length - 1,
              }));
            }}
          >
            <Plus className={`size-4`} />
            {isSpanish ? "Agregar Documento" : "Add Document"}
          </button>
          {companyName === 'tudefrigo' && (
            <button
              className="py-1.5 text-xs flex items-center gap-1 bg-secondary rounded-full pl-2 pr-3"
              onClick={handleAddY025TuDeFrigo}
              type="button"
            >
              <Plus className="size-4" />
              Agregar Y025
            </button>
          )}
          {companyName === 'el mosca' && (
            <button
              className="py-1.5 text-xs flex items-center gap-1 bg-secondary rounded-full pl-2 pr-3"
              onClick={handleAddY025ElMosca}
              type="button"
            >
              <Plus className="size-4" />
              Agregar Y025
            </button>
          )}
        </div>

        <div className="flex-1 overflow-auto">
          <DeclarationDocumentsTableContent
            declarationIndex={declarationIndex}
            isSpanish={isSpanish}
            declaration={declaration}
            editableData={editableData}
            setEditableData={handleSetEditableData}
            setIsDirty={setIsDirty}
            openDocModalIndex={openDocModalIndex}
            setOpenDocModalIndex={setOpenDocModalIndex}
            openTipoModal={openTipoModal}
            setOpenTipoModal={setOpenTipoModal}
          />
        </div>
      </div>
    </div>
  );
};

